import { useEffect, useRef } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import {
  searchPageSizeAtom,
  searchQueryAtom,
  selectedResultsAtom,
} from 'packages/omnitag/state';
import { OmnitagResultItem } from 'packages/lib/api/omnitag/contract/omnitag-search';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { updateTextSearchTerm } from 'packages/omnitag/text-search/search-ui';

const LOCAL_STORAGE_KEY_QUERY = 'polaris_search_query';
const LOCAL_STORAGE_KEY_SELECTION = 'polaris_selected_results';

export const useRememberSelection = (): void => {
  const [searchQuery, setSearchQuery] = useAtom(searchQueryAtom);
  const [selectedResults, setSelectedResults] = useAtom(selectedResultsAtom);
  const pageSize = useAtomValue(searchPageSizeAtom);
  const isMounted = useRef(false);

  const match = useRouteMatch();
  const history = useHistory();

  // On initial mount, try to rehydrate selection from localStorage
  useEffect(() => {
    isMounted.current = false;

    if (
      history.location.pathname === '/polaris/tools' ||
      history.location.pathname === '/polaris/data-requests'
    ) {
      return;
    }

    const storedQuery = localStorage.getItem(LOCAL_STORAGE_KEY_QUERY);
    const storedSelectionJSON = localStorage.getItem(
      LOCAL_STORAGE_KEY_SELECTION
    );

    if (storedQuery) {
      try {
        const storedQueryObject = JSON.parse(storedQuery);
        updateTextSearchTerm(
          storedQueryObject.text,
          history,
          match,
          1,
          pageSize
        );
        setSearchQuery(storedQueryObject);
      } catch (e) {
        console.error('Failed to parse stored query from localStorage', e);
      }
    }

    if (storedQuery && storedSelectionJSON) {
      try {
        const storedSelection = new Set<OmnitagResultItem>(
          JSON.parse(storedSelectionJSON)
        );
        setSelectedResults(storedSelection);
      } catch (e) {
        console.error('Failed to parse stored selection from localStorage', e);
      }
    }

    isMounted.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Persist search query and selection to localStorage when they change
  useEffect(() => {
    // Do not persist on the initial render, wait for rehydration.
    if (!isMounted.current) {
      return;
    }
    localStorage.setItem(LOCAL_STORAGE_KEY_QUERY, JSON.stringify(searchQuery));
    localStorage.setItem(
      LOCAL_STORAGE_KEY_SELECTION,
      JSON.stringify(Array.from(selectedResults))
    );
  }, [searchQuery, selectedResults]);
};
